import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { verifyOtp, sendOtp, setPassword as savePassword } from "../../services/api/authApi";
import { useAuth } from "../../Context/AuthContext";
import { extractErrorMessage } from "../../services/api/errorHandler";
import AuthLayout from "./components/AuthLayout";
import OtpInputs, { OTP_LENGTH } from "./components/OtpInputs";
import useResendCountdown, { formatCountdown } from "./components/useResendCountdown";
import {
  AlertIcon,
  BackArrowIcon,
  CheckIcon,
  ClockIcon,
  EditIcon,
  ResendIcon,
  ShieldIcon,
  SpinnerIcon,
} from "./components/AuthIcons";
import { dashboardPathFor, maskPhone, toEnglishDigits } from "./components/loginUtils";

function LoginCode() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [phone, setPhone] = useState("");
  const [authenticatedUser, setAuthenticatedUser] = useState(null);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, token } = useAuth();
  const isPasswordSetup = location.state?.setupPassword === true;
  const isPasswordReset = location.state?.resetPassword === true;
  const requiresPasswordForm = isPasswordSetup || isPasswordReset || location.state?.needsPassword === true;
  const verifyingRef = useRef(false);
  const { secondsLeft, canResend, start } = useResendCountdown();

  useEffect(() => {
    if (location.state?.phone) {
      setPhone(location.state.phone);
    } else {
      navigate("/LoginNumber", { replace: true });
    }
  }, [location, navigate]);

  const handleVerify = async (codeValue) => {
    const normalized = toEnglishDigits(codeValue).replace(/\D/g, "");
    if (verifyingRef.current || loading) return;

    if (!normalized) {
      setError("کد ورود را وارد کنید");
      return;
    }

    if (!new RegExp(`^\\d{${OTP_LENGTH}}$`).test(normalized)) {
      setError(`کد ورود باید ${OTP_LENGTH} رقم باشد`);
      return;
    }

    verifyingRef.current = true;
    setLoading(true);
    setError("");
    setNotice("");
    try {
      // Same backend endpoint + payload shape as before.
      const response = await verifyOtp(phone, normalized, null, null);
      if (response.success && response.data) {
        setNotice("ورود موفقیت‌آمیز بود؛ در حال انتقال...");
        login(response.data.token, response.data.user);
        setAuthenticatedUser(response.data.user);
        if (requiresPasswordForm) {
          setShowPasswordPrompt(true);
        } else {
          navigate(dashboardPathFor(response.data.user), { replace: true });
        }
      } else {
        setError(extractErrorMessage({ response: { data: response } }, "کد ورود نادرست است"));
      }
    } catch (err) {
      setError(extractErrorMessage(err, "کد ورود نادرست است"));
    } finally {
      setLoading(false);
      verifyingRef.current = false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify(code);
  };

  const handleResend = async () => {
    if (!canResend || resendLoading || loading) return;
    setError("");
    setNotice("");
    setResendLoading(true);
    try {
      // Same backend endpoint as before — UI-only redesign.
      const response = await sendOtp(phone);
      if (response.success) {
        setCode("");
        setNotice("کد جدید ارسال شد");
        start();
      } else {
        setError(extractErrorMessage({ response: { data: response } }, "خطا در ارسال مجدد کد"));
      }
    } catch (err) {
      setError(extractErrorMessage(err, "خطای شبکه. لطفاً دوباره تلاش کنید"));
    } finally {
      setResendLoading(false);
    }
  };

  const backToNumber = () => {
    // Return with the current number preserved so the user can edit it.
    navigate("/LoginNumber", { state: { phone } });
  };

  const continueToDashboard = () => {
    navigate(dashboardPathFor(authenticatedUser), { replace: true });
  };

  const handleOptionalPassword = async (event) => {
    event.preventDefault();
    if (newPassword.length < 8) {
      setPasswordError("رمز عبور باید حداقل ۸ کاراکتر باشد");
      return;
    }
    if (newPassword !== passwordConfirmation) {
      setPasswordError("تکرار رمز عبور با رمز اصلی یکسان نیست");
      return;
    }
    setSavingPassword(true);
    setPasswordError("");
    try {
      const response = await savePassword(newPassword);
      if (!response.success || !response.data) throw new Error(response.message || "ذخیره رمز عبور ناموفق بود");
      login(response.data.token || token, response.data.user);
      navigate(dashboardPathFor(response.data.user), { replace: true });
    } catch (err) {
      setPasswordError(extractErrorMessage(err, "ذخیره رمز عبور ناموفق بود"));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <AuthLayout
      badgeIcon={<ShieldIcon size={26} />}
      title={isPasswordSetup ? "ثبت رمز عبور" : isPasswordReset ? "بازیابی رمز عبور" : "کد تایید را وارد کنید"}
      subtitle={
        phone ? (
          <>
            {isPasswordSetup ? "برای ثبت رمز عبور، کد ۶ رقمی ارسال‌شده به شماره " : isPasswordReset ? "برای بازیابی رمز عبور، کد ۶ رقمی ارسال‌شده به شماره " : "کد ۶ رقمی به شماره "}
            <bdi className="auth-phone-chip">{maskPhone(phone)}</bdi>
            {isPasswordSetup || isPasswordReset ? " را وارد کنید" : " پیامک شد"}
          </>
        ) : (
          "کد ۶ رقمی پیامک‌شده را وارد کنید"
        )
      }
    >
      <button className="auth-back" type="button" onClick={backToNumber} aria-label="بازگشت و ویرایش شماره موبایل">
        <BackArrowIcon size={20} />
      </button>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <OtpInputs
          value={code}
          onChange={(next) => {
            setCode(next);
            if (error) setError("");
          }}
          onComplete={handleVerify}
          disabled={loading}
          hasError={!!error}
        />

        {error && (
          <p className="auth-alert auth-alert-error" role="alert">
            <AlertIcon size={18} />
            <span>{error}</span>
          </p>
        )}

        {notice && !error && (
          <p className="auth-alert auth-alert-success" role="status">
            <CheckIcon size={18} />
            <span>{notice}</span>
          </p>
        )}

        <button
          className="auth-btn auth-btn-primary"
          type="submit"
          disabled={loading || code.length !== OTP_LENGTH}
        >
          {loading && <SpinnerIcon />}
          {loading ? "در حال تایید..." : requiresPasswordForm ? "تایید و ادامه" : "تایید و ورود"}
        </button>

        <div className="auth-resend">
          {canResend ? (
            <button
              className="auth-btn auth-btn-ghost"
              type="button"
              onClick={handleResend}
              disabled={resendLoading || loading}
            >
              {resendLoading ? <SpinnerIcon size={16} /> : <ResendIcon size={16} />}
              {resendLoading ? "در حال ارسال..." : "ارسال مجدد کد"}
            </button>
          ) : (
            <p className="auth-countdown">
              <ClockIcon size={16} />
              <span>
                ارسال مجدد تا <strong>{formatCountdown(secondsLeft)}</strong> دیگر
              </span>
            </p>
          )}
        </div>

        <div className="auth-divider">یا</div>

        <button
          className="auth-btn auth-btn-code"
          type="button"
          onClick={() => navigate("/LoginPassword", { state: { phone } })}
          disabled={loading || !phone}
        >
          ورود با رمز عبور
        </button>

        <div className="auth-edit-row">
          <button className="auth-btn auth-btn-outline" type="button" onClick={backToNumber}>
            <EditIcon size={16} />
            ویرایش شماره موبایل
          </button>
        </div>
      </form>

      {showPasswordPrompt && (
        <div className="auth-modal-backdrop" role="presentation">
          <section className="auth-modal" role="dialog" aria-modal="true" aria-labelledby="optional-password-title">
            <div className="auth-modal-icon"><ShieldIcon size={24} /></div>
            <h2 id="optional-password-title" className="auth-modal-title">{isPasswordReset ? "ثبت رمز عبور جدید" : "ثبت رمز عبور"}</h2>
            <p className="auth-modal-text">{isPasswordReset ? "رمز عبور جدید خود را برای ورودهای بعدی تعیین کنید." : "برای ورودهای بعدی یک رمز عبور تعیین کنید. این مرحله اختیاری است."}</p>
            <form className="auth-form" onSubmit={handleOptionalPassword} noValidate>
              <div className="auth-field"><label className="auth-label" htmlFor="optional-password">رمز عبور جدید</label><div className="auth-input-wrap"><input id="optional-password" className="auth-input" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setPasswordError(""); }} disabled={savingPassword} autoFocus /></div><p className="auth-hint">حداقل ۸ کاراکتر وارد کنید</p></div>
              <div className="auth-field"><label className="auth-label" htmlFor="optional-password-confirm">تکرار رمز عبور</label><div className="auth-input-wrap"><input id="optional-password-confirm" className="auth-input" type="password" autoComplete="new-password" value={passwordConfirmation} onChange={(e) => { setPasswordConfirmation(e.target.value); setPasswordError(""); }} disabled={savingPassword} /></div></div>
              {passwordError && <p className="auth-alert auth-alert-error" role="alert"><AlertIcon size={18} /><span>{passwordError}</span></p>}
              <button className="auth-btn auth-btn-primary" type="submit" disabled={savingPassword || !newPassword || !passwordConfirmation}>{savingPassword && <SpinnerIcon />}{savingPassword ? "در حال ذخیره..." : isPasswordReset ? "ثبت رمز عبور جدید" : "ثبت رمز عبور"}</button>
              <button className="auth-btn auth-btn-ghost" type="button" onClick={continueToDashboard} disabled={savingPassword}>فعلاً رد می‌کنم</button>
            </form>
          </section>
        </div>
      )}
    </AuthLayout>
  );
}

export default LoginCode;
